// Debug helper: finds elements causing horizontal overflow.
// Enable with: ?debugOverflow=1

export function installOverflowDebugger() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  if (params.get('debugOverflow') !== '1') return;

  const OUTLINE = '2px solid rgba(239, 68, 68, 0.95)';
  const OUTLINE_OFFSET = '2px';

  const seen = new WeakSet();

  const clearMarks = () => {
    document.querySelectorAll('[data-overflow-debug="1"]').forEach((el) => {
      el.removeAttribute('data-overflow-debug');
      el.style.outline = '';
      el.style.outlineOffset = '';
    });
  };

  const isVisible = (el) => {
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const scan = () => {
    try {
      const root = document.documentElement;
      const vw = root.clientWidth;
      const slop = 1; // px tolerance

      clearMarks();

      const offenders = [];
      const all = document.body ? document.body.querySelectorAll('*') : [];

      all.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        if (!isVisible(el)) return;

        // Skip elements that often extend for animation but are clipped already.
        const cs = window.getComputedStyle(el);
        if (cs.position === 'fixed' && cs.left === '0px' && cs.right === '0px') {
          // still can overflow, keep scanning
        }

        const rect = el.getBoundingClientRect();
        if (rect.width <= vw + slop && rect.left >= -slop && rect.right <= vw + slop) return;

        // Ignore huge full-screen backdrops that are correctly anchored.
        if (cs.position === 'fixed' && rect.left <= -slop && rect.right >= vw + slop && rect.width <= vw + 4) {
          return;
        }

        offenders.push({
          el,
          left: Math.round(rect.left),
          right: Math.round(rect.right),
          width: Math.round(rect.width)
        });

        el.setAttribute('data-overflow-debug', '1');
        el.style.outline = OUTLINE;
        el.style.outlineOffset = OUTLINE_OFFSET;
      });

      offenders
        .sort((a, b) => (b.width - vw) - (a.width - vw))
        .slice(0, 20)
        .forEach((o, idx) => {
          if (!seen.has(o.el)) {
            seen.add(o.el);
            // eslint-disable-next-line no-console
            console.warn(`[overflow-x] offender #${idx + 1}`, {
              tag: o.el.tagName.toLowerCase(),
              id: o.el.id || undefined,
              class: o.el.className || undefined,
              left: o.left,
              right: o.right,
              width: o.width,
              viewport: vw
            });
          }
        });

      // eslint-disable-next-line no-console
      console.info(`[overflow-x] scan complete: ${offenders.length} offender(s).`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[overflow-x] scan failed', e);
    }
  };

  let raf = 0;
  const schedule = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      scan();
    });
  };

  window.__overflowDebugScan = scan;

  // Initial + after layout settles.
  schedule();
  setTimeout(schedule, 600);

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });

  // Re-scan when DOM changes (routing / dialogs).
  const obs = new MutationObserver(() => schedule());
  if (document.body) obs.observe(document.body, { subtree: true, childList: true, attributes: true });

  // eslint-disable-next-line no-console
  console.info('[overflow-x] debugger enabled. Use window.__overflowDebugScan() to rescan.');
}
